// internationalization
import i18n from 'i18next';

// components
import {
  extractErrorCode,
  extractHttpStatus,
  extractProblemDetails,
  getErrorSeverity,
  isFetchBaseQueryError,
  isValidationError,
  normalizeErrorCode,
} from '@/utils/apiErrorUtils';

// relative
import { showSnackbar } from './snackbarSlice';

import type { Middleware } from '@reduxjs/toolkit';
import { isRejectedWithValue } from '@reduxjs/toolkit';

function resolveMessage(key: string, ns: string): string | null {
  if (i18n.exists(key, { ns })) {
    return i18n.t(key, { ns }) as string;
  }
  return null;
}

/**
 * Maps RTK Query slice reducer paths to their i18next error namespaces.
 * The middleware extracts the slice name from the action type
 * (`{reducerPath}/executeQuery/rejected`) and looks up the right namespace
 * automatically — no per-component suppression or coupling needed.
 *
 * Add an entry here when a new API slice gets its own error translation file.
 */
const API_NAMESPACE_MAP: Record<string, string> = {
  recalcV2ApiSlice: 'recalc-errors',
};

/**
 * Endpoint names that should not trigger a snackbar on failure.
 * Use this for background/polling queries where a silent failure is acceptable
 * and the UI already reflects the error state inline.
 *
 * Telemetry (App Insights) still fires for all suppressed endpoints.
 * Add entries here — never in components.
 */
const SNACKBAR_SUPPRESSED_ENDPOINTS = new Set<string>([
  // example: 'getSystemStatus',
]);

function buildErrorMessage(
  payload: unknown,
  apiNamespace: string | undefined,
): {
  message: string;
  severity: 'warning' | 'error';
} {
  if (!isFetchBaseQueryError(payload)) {
    return {
      message:
        resolveMessage('generic', 'http-errors') ??
        'An unexpected error occurred',
      severity: 'error',
    };
  }

  const status = extractHttpStatus(payload);
  const problemDetails = extractProblemDetails(payload);
  const severity = getErrorSeverity(status);

  if (problemDetails) {
    // Domain error with a structured error code.
    // Resolution order: API-specific namespace -> backend-errors -> http-errors
    const errorCode = extractErrorCode(problemDetails);
    if (errorCode) {
      const key = normalizeErrorCode(errorCode);
      const message =
        (apiNamespace ? resolveMessage(key, apiNamespace) : null) ??
        resolveMessage(key, 'backend-errors') ??
        resolveMessage(String(status ?? 400), 'http-errors') ??
        resolveMessage('generic', 'http-errors');
      if (message) return { message, severity };
    }

    // Standard ASP.NET model-binding validation errors (code: null per field).
    if (isValidationError(problemDetails)) {
      const message =
        resolveMessage(String(status ?? 400), 'http-errors') ??
        resolveMessage('generic', 'http-errors');
      return { message: message ?? '', severity: 'warning' };
    }
  }

  // HTTP status fallback.
  const message =
    (status ? resolveMessage(String(status), 'http-errors') : null) ??
    resolveMessage('generic', 'http-errors') ??
    'An unexpected error occurred';

  return { message, severity };
}

/**
 * Global RTK Query error middleware.
 *
 * Intercepts all rejected RTK Query actions, resolves a translated message
 * using the slice's registered error namespace (via API_NAMESPACE_MAP), and
 * dispatches a snackbar. Also logs every failure to Application Insights.
 */
export const apiErrorMiddleware: Middleware =
  (storeApi) => (next) => (action) => {
    if (isRejectedWithValue(action)) {
      const meta = (
        action as {
          meta?: {
            arg?: { endpointName?: string; type?: string };
          };
        }
      ).meta;

      const endpointName = meta?.arg?.endpointName;

      // Extract the slice name from the action type to find the right namespace.
      const sliceName = (action.type as string).split('/')[0];
      const apiNamespace = API_NAMESPACE_MAP[sliceName];

      const suppressSnackbar =
        endpointName !== undefined &&
        SNACKBAR_SUPPRESSED_ENDPOINTS.has(endpointName);

      if (!suppressSnackbar) {
        const { message, severity } = buildErrorMessage(
          action.payload,
          apiNamespace,
        );
        storeApi.dispatch(showSnackbar({ message, severity }));
      }
    }

    return next(action);
  };
